// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Fleet overview — layout follows the AI Summit demo mock
// (ui-mocks/official/AI Summit demo (No Eng Work).html): a hairline KPI tile
// row above a grid of per-site cards, each with a status pill, a capacity
// line, a right-aligned "% · PUE" figure, and a full-width P1-P4 badge strip.
// The KPI tiles and per-site figures read useFleetMetrics, which returns
// nulls until the milestone-2 endpoint lands, so they sit behind
// `fleetOverviewMetricsEnabled` (default off) and show "not available" when
// on; the P1 alarms KPI and the P1-P4 strips are real and always shown.
//
// This is PR 1 of the fleet-overview-map work — layout only, no map. See
// docs/design/fleet-overview-map.md for the full three-PR plan; the map
// section lands between the KPI row and the site cards in PR 2.

import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import type { Severity } from '../../types/alarm';
import type { SitePosture } from '../../types/sitePosture';
import { POSTURE_LABEL } from '../../types/sitePosture';
import { useFleetAlarms } from '../../hooks/useAlarms';
import { priorityToSeverity } from '../../services/alarmMappers';
import { useAuth } from '../../hooks/useAuth';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useFlagOn } from '../../hooks/useFlagOn';
import { useFleetMetrics, formatMeasure, NO_SITE_METRICS } from '../../hooks/useFleetMetrics';
import type { FleetMeasure, FleetMetricsResult } from '../../hooks/useFleetMetrics';
import { PageHead } from '../../components/shell/ScreenHelpers';
import FleetMap from '../../components/fleet-map/FleetMap';
import { displaySiteKey } from '../../config/siteDisplayOverrides';
import type { FleetDemoScenario } from './demo/fleetDemoScenarios';

const SEVERITIES: Severity[] = ['P1', 'P2', 'P3', 'P4'];

/** Default skeleton row count when the site list itself isn't known yet. */
const SKELETON_ROWS = 4;

/**
 * Loading placeholders for the two data-backed regions of the page.
 *
 * They are split either side of the map because the map is NOT a skeleton
 * while loading: site locations come from the site list, which is already
 * resolved when this page mounts, so the real map renders immediately and
 * only its alarm-reporting surfaces wait. Keeping the KPI and card
 * placeholders the same shape as the real thing means nothing shifts when
 * the counts land.
 */
function FleetKpiSkeleton({ showMetrics }: { showMetrics: boolean }) {
  return (
    <div className={kpiRowClass(showMetrics)} aria-hidden="true">
      {Array.from({ length: showMetrics ? 4 : 1 }).map((_, i) => (
        <div className="fleet-kpi" key={i}>
          <div className="fleet-kpi-head">
            <SkeletonPlaceholder className="fleet-kpi-dot-skel" />
            <SkeletonText width="60%" />
          </div>
          <div className="fleet-kpi-value-row">
            <SkeletonPlaceholder className="fleet-kpi-value-skel" />
          </div>
          <SkeletonText width="80%" className="fleet-kpi-sub-skel" />
        </div>
      ))}
    </div>
  );
}

function FleetCardsSkeleton({
  rows = SKELETON_ROWS,
  showMetrics,
}: {
  rows?: number;
  showMetrics: boolean;
}) {
  return (
    <div className="fleet-sites" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          className="fleet-site-card fleet-site-card--skel"
          key={i}
          data-testid="fleet-site-skeleton-row"
        >
          <div className="fleet-site-card-head">
            <div className="fleet-site-name-lines">
              <SkeletonText width="70%" />
              <SkeletonText width="50%" />
            </div>
            <SkeletonPlaceholder className="fleet-site-dot-skel" />
          </div>
          {showMetrics && (
            <div className="fleet-site-card-figures">
              <SkeletonText width="60%" />
            </div>
          )}
          <div className="fleet-site-sevs">
            {SEVERITIES.map(sev => (
              <SkeletonPlaceholder key={sev} className="fleet-sev-skel" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

type SevCounts = Record<Severity, number>;

interface FleetSite {
  siteId: string;
  siteKey: string;
  label: string;
  /** From the twin, or scripted by a demo scenario. */
  coordinate?: { lat: number; lon: number; place?: string };
  /** Street line, from the twin. Absent on demo sites. */
  addressLine1?: string;
  /**
   * Scripted by a demo scenario; on the real fleet this is a placeholder —
   * see resolvePosture, which derives the posture actually shown from live
   * P1 counts instead.
   */
  posture: SitePosture;
}

export interface FleetOverviewProps {
  /**
   * Render a scripted fleet instead of the signed-in user's.
   *
   * Set only by the /fleet/demo/:scenario screens. When present the page
   * takes its sites AND its alarm counts from the scenario and makes no
   * alarm request at all, so demo numbers can never be mistaken for live
   * ones (see demo/fleetDemoScenarios.ts). Unset on /fleet — that path is
   * untouched by this prop.
   */
  demo?: FleetDemoScenario;
}

/** Stable empty list, so the demo branch's `useFleetAlarms` never re-fetches. */
const NO_SITES: never[] = [];
const NO_SITE_IDS: string[] = [];

function emptyCounts(): SevCounts {
  return { P1: 0, P2: 0, P3: 0, P4: 0, Unknown: 0 };
}

/** A KPI with no real source yet — shows "—" and why, never a fake number. */
const NOT_AVAILABLE = 'Not available — no data source yet';
/** The source exists but the request failed: a different gap, named as such. */
const LOAD_FAILED = 'Not available — could not load fleet metrics';

/** What a tile says under "—": why there is no figure. */
function unavailableReason({ error }: FleetMetricsResult): string {
  return error ? LOAD_FAILED : NOT_AVAILABLE;
}

/**
 * A fleet-wide figure, laid out as the design draws it: the value with its
 * unit symbol glued on ("72%", "1.38"), then the tile's caption word ("used",
 * "uptime", "avg"). With null: "—", the same caption so the row keeps its
 * shape, and `unavailable` saying why. The dot is green only for a real one.
 */
function MetricTile({
  label,
  caption,
  measure,
  unavailable,
  testId,
}: {
  label: string;
  /** The word after the value, e.g. "used". */
  caption: string;
  measure: FleetMeasure | null;
  /** Shown under "—" when there is no measure. */
  unavailable: string;
  testId: string;
}) {
  return (
    <div className="fleet-kpi" data-testid={testId}>
      <div className="fleet-kpi-head">
        <span
          className="fleet-kpi-dot"
          style={{ background: measure ? 'var(--st-ok)' : 'var(--st-idle)' }}
        />
        <span className="fleet-kpi-label">{label}</span>
      </div>
      <div className="fleet-kpi-value-row">
        <span className="fleet-kpi-value">{measure ? tileValue(measure) : '—'}</span>
        <span className="fleet-kpi-unit">{caption}</span>
      </div>
      {!measure && <div className="fleet-kpi-sub">{unavailable}</div>}
    </div>
  );
}

/**
 * "72%" or "1.38": every fleet tile is a percentage or dimensionless (see the
 * FleetMetrics field docs), so the unit symbol is glued to the number.
 */
function tileValue({ value, unit }: FleetMeasure): string {
  return `${value.toLocaleString()}${unit}`;
}

/**
 * The KPI row is one tile wide while the fleet metrics are flagged off, so it
 * must not stretch that lone tile across the page.
 */
function kpiRowClass(showMetrics: boolean): string {
  return showMetrics ? 'fleet-kpis' : 'fleet-kpis fleet-kpis--compact';
}

/**
 * The card's right-aligned "% · PUE" line. "not available" while both are
 * null; otherwise each half shows its figure or "—".
 */
function usageLine(used: FleetMeasure | null, pue: FleetMeasure | null): string {
  if (!used && !pue) return 'not available';
  return `${formatMeasure(used)} · ${formatMeasure(pue)}`;
}

/** Live dateline, or one that says plainly that the fleet is scripted. */
function dateline(demo?: FleetDemoScenario): string {
  return demo ? `Demo scenario ${demo.id} · scripted fleet, not live data` : 'All regions · live';
}

export default function FleetOverview({ demo }: FleetOverviewProps = {}) {
  const navigate = useNavigate();
  const { sites } = useAuth();
  const { records, failedSiteIds = NO_SITE_IDS, loading } = useFleetAlarms(demo ? NO_SITES : sites);
  const { setSite, setFleet, isFleet } = useActiveSite();
  // IT capacity, fleet availability, PUE and per-site capacity have no backend
  // yet (GA-10 triage). Hidden rather than shown as "—" until it ships.
  const showMetrics = useFlagOn('fleetOverviewMetricsEnabled');
  // A scripted fleet makes no data request, same as the alarms above: once
  // this hook fetches, /fleet/demo must not ask for real metrics on fake sites.
  const siteIds = useMemo(() => (demo ? NO_SITES : sites.map(s => s.site_id)), [demo, sites]);
  const metricsResult = useFleetMetrics(siteIds);
  const { metrics } = metricsResult;

  const openSite = (siteId: string) => {
    if (demo) return;
    setSite(siteId);
    navigate('/home/all');
  };

  const active = useRef({ isFleet, setFleet });
  active.current = { isFleet, setFleet };
  useEffect(() => {
    if (demo || active.current.isFleet) return;
    active.current.setFleet();
  }, [demo]);

  const fleetSites: FleetSite[] = useMemo(
    () =>
      demo
        ? demo.sites.map(s => ({
            siteId: s.siteId,
            siteKey: s.siteKey,
            label: s.label,
            coordinate: { lat: s.lat, lon: s.lon, place: s.place },
            posture: s.posture,
          }))
        : sites.map(s => ({
            siteId: s.site_id,
            siteKey: s.site_key,
            label: s.label,
            coordinate:
              s.latitude != null && s.longitude != null
                ? { lat: s.latitude, lon: s.longitude }
                : undefined,
            addressLine1: s.address?.addressLine1 ?? undefined,
            posture: 'healthy' as const,
          })),
    [sites, demo]
  );

  const bySite = useMemo(() => {
    const acc: Record<string, SevCounts> = {};
    if (demo) {
      for (const site of demo.sites) acc[site.siteId] = site.counts;
      return acc;
    }
    for (const site of fleetSites) {
      acc[site.siteId] = emptyCounts();
    }
    for (const record of records) {
      const counts = acc[record.site_id ?? ''];
      if (counts) counts[priorityToSeverity(record.priority)] += 1;
    }
    return acc;
  }, [records, fleetSites, demo]);

  const openEscalations = useMemo(() => {
    let p1 = 0;
    for (const counts of Object.values(bySite)) p1 += counts.P1;
    return p1;
  }, [bySite]);

  const resolvePosture = (site: FleetSite): SitePosture => {
    if (demo) return site.posture;
    const counts = bySite[site.siteId] ?? emptyCounts();
    return counts.P1 > 0 ? 'at-risk' : 'healthy';
  };

  const mapSites = fleetSites.map(site => ({
    siteId: site.siteId,
    siteKey: site.siteKey,
    label: site.label,
    coordinate: site.coordinate,
    addressLine1: site.addressLine1,
    posture: resolvePosture(site),
    counts: bySite[site.siteId] ?? emptyCounts(),
    metrics: metrics.sites[site.siteId] ?? NO_SITE_METRICS,
  }));

  // The metrics only matter while they are shown; hidden, their fetch state
  // must not hold the whole page on a skeleton.
  if (loading || (showMetrics && metricsResult.loading)) {
    return (
      <>
        <PageHead title="Fleet overview" dateline={dateline(demo)} />
        <FleetKpiSkeleton showMetrics={showMetrics} />
        {/* Map and cards share one panel — see the .fleet-panel comment in
            shell.css. */}
        <div className="fleet-panel">
          <FleetMap sites={mapSites} loading showMetrics={showMetrics} />
          <FleetCardsSkeleton rows={fleetSites.length || SKELETON_ROWS} showMetrics={showMetrics} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead title="Fleet overview" dateline={dateline(demo)} />

      <div className={kpiRowClass(showMetrics)}>
        {showMetrics && (
          <>
            <MetricTile
              label="IT capacity"
              caption="used"
              measure={metrics.itCapacity}
              unavailable={unavailableReason(metricsResult)}
              testId="fleet-kpi-it-capacity"
            />
            <MetricTile
              label="Fleet availability"
              caption="uptime"
              measure={metrics.availability}
              unavailable={unavailableReason(metricsResult)}
              testId="fleet-kpi-availability"
            />
            <MetricTile
              label="Fleet-level PUE"
              caption="avg"
              measure={metrics.pue}
              unavailable={unavailableReason(metricsResult)}
              testId="fleet-kpi-pue"
            />
          </>
        )}

        <div className="fleet-kpi">
          <div className="fleet-kpi-head">
            <span
              className="fleet-kpi-dot"
              style={{ background: openEscalations > 0 ? 'var(--st-fault)' : 'var(--st-ok)' }}
            />
            <span className="fleet-kpi-label">P1 alarms</span>
          </div>
          <div className="fleet-kpi-value-row">
            <span className="fleet-kpi-value" data-testid="fleet-p1-total">
              {openEscalations}
              {failedSiteIds.length > 0 && '+'}
            </span>
          </div>
          <div className="fleet-kpi-sub">
            {failedSiteIds.length > 0
              ? `${failedSiteIds.length} site(s) could not be read`
              : 'Across all sites'}
          </div>
        </div>
      </div>

      {/* The map and the site cards are one surface, not two stacked boxes:
          the cards are the map's legend in card form, so a seam between them
          reads as an unrelated panel (see .fleet-panel in shell.css). */}
      <div className="fleet-panel">
        <FleetMap sites={mapSites} onSelectSite={openSite} showMetrics={showMetrics} />

        <div className="fleet-sites">
          {fleetSites.map(site => {
            const counts = bySite[site.siteId] ?? emptyCounts();
            const posture = resolvePosture(site);
            const siteMetrics = metrics.sites[site.siteId] ?? NO_SITE_METRICS;
            return (
              <button
                key={site.siteId}
                type="button"
                className="fleet-site-card"
                data-testid="fleet-site"
                data-site-id={site.siteId}
                onClick={() => openSite(site.siteId)}
              >
                <div className="fleet-site-card-head">
                  <div className="fleet-site-name-lines">
                    <div className="fleet-site-name" data-testid="fleet-site-name">
                      {site.label}
                    </div>
                    <div className="fleet-site-key">{displaySiteKey(site.siteKey)}</div>
                    {site.addressLine1 && (
                      <div className="fleet-site-address" data-testid="fleet-site-address">
                        {site.addressLine1}
                      </div>
                    )}
                  </div>
                  <span
                    className={`fleet-site-posture fleet-site-posture--${posture}`}
                    data-testid="fleet-site-posture"
                  >
                    <span className={`fleet-site-dot fleet-site-dot--${posture}`} />
                    {POSTURE_LABEL[posture]}
                  </span>
                </div>

                {showMetrics && (
                  <div className="fleet-site-card-figures">
                    <div className="fleet-site-capacity-value" data-testid="fleet-site-capacity">
                      {formatMeasure(siteMetrics.capacity)}
                    </div>
                    <div className="fleet-site-capacity-sub" data-testid="fleet-site-usage">
                      {usageLine(siteMetrics.used, siteMetrics.pue)}
                    </div>
                  </div>
                )}

                <div className="fleet-site-sevs">
                  {SEVERITIES.map(sev => (
                    <span
                      key={sev}
                      className={`hs-sev hs-sev-${sev}`}
                      data-testid={`fleet-count-${sev.toLowerCase()}`}
                    >
                      <span className="dot" />
                      {sev} · {counts[sev]}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
